import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Fade
} from '@mui/material';
import {
  ArrowBack,
} from '@mui/icons-material';
import LoadingImage from '../components/LoadingImage';

const DoubleDeck: React.FC = () => {
  // SEO Meta Tags
  useEffect(() => {
    document.title = "Çift Katlı Sistem (Double Deck) Montajı - Kale Treyler Frigo | Mersin";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de frigo dorse araçlar için çift katlı (double deck) sistem montajı ve bakımı. Taşıma kapasitesini ikiye katlayan profesyonel çözümler.");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "double deck sistem, çift katlı dorse, Mersin dorse tamiri, dorse modifikasyon, tır dorse çift kat");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Çift Katlı Sistem (Double Deck) Montajı - Kale Treyler Frigo | Mersin");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Mersin'de frigo dorse araçlar için çift katlı (double deck) sistem montajı ve bakımı. Taşıma kapasitesini ikiye katlayan profesyonel çözümler.");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/hizmetler/double-deck');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const process = [
    {
      step: 1,
      title: 'İhtiyaç Analizi',
      description: 'Müşterinin ihtiyaçlarına göre en uygun çift katlı sistem konfigürasyonunu belirliyoruz.'
    },
    {
      step: 2,
      title: 'Teknik Çizim',
      description: 'Araç ölçülerine uygun teknik çizim ve montaj planı hazırlıyoruz.'
    },
    {
      step: 3,
      title: 'Malzeme Temini',
      description: 'Kaliteli double deck sistem malzemelerini tedarik ediyoruz.'
    },
    {
      step: 4,
      title: 'Profesyonel Montaj',
      description: 'Uzman ekibimizle profesyonel montaj hizmeti gerçekleştiriyoruz.'
    },
    {
      step: 5,
      title: 'Test ve Teslim',
      description: 'Sistemin düzgün çalıştığını test ediyor ve müşteriye teslim ediyoruz.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(30, 58, 138, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(30, 58, 138, 0.1) 0%, transparent 50%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4, lg: 6 } }}>
          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Button
                    component={RouterLink}
                    to="/hizmetler"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 3, textTransform: 'none' }}
                  >
                    Hizmetlere Dön
                  </Button>
                  <Typography variant="h1" component="h1" gutterBottom sx={{ 
                    fontWeight: 800, 
                    color: 'text.primary',
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    mb: 3
                  }}>
                    Çift Katlı Sistem (Double Deck System)
                  </Typography>
                  <Typography variant="h5" paragraph sx={{ 
                    mb: 4, 
                    color: 'text.secondary',
                    lineHeight: 1.6
                  }}>
Çift katlı bir çözüm, yüklerin üst üste istiflenemediği durumlarda (örneğin kutulu paletler) idealdir. İkinci bir kat oluşturarak aracın tüm kapasitesini kullanabilirsiniz . Çözümlerimiz, farklı yükleme yüksekliklerinde bile kullanılabilir.                  </Typography>
                  
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'transform 0.3s ease',
                    }
                  }}
                >
                  <LoadingImage
                    src="/24.jpg"
                    alt="Double Deck Çift Katlı Sistem"
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      p: 3,
                      color: 'white',
                    }}
                  >
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Çift Katlı Sistem
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Alt ve üst kat sistemleri için uzman çözümler
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Product Section */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 2, md: 4, lg: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
          <Grid item xs={12} lg={6}>
            <Fade in timeout={1000}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.3s ease',
                  }
                }}
              >
                <LoadingImage
                  src="/36.jpg"
                  alt="LayerLok XP Sistem"
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Fade>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Fade in timeout={1500}>
              <Box>
                <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 4 }}>
                  Yük ve Hacmi Maksimize Edin
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                Çift katlı sistem tek veya çift olarak tamamen gömülü, yarı gömülü veya yüzeye monte olarak mevcuttur. Sadece 9,25 mm profil yüksekliğine sahip tamamen gömülü raylar, duvar kalınlığı yalnızca 24 mm (veya daha fazla) olan kapalı kasa veya soğutmalı araçlar/soğutuculu araçlar için bir dünya olasılık sunar. Sabit özellikleri ve PIEK sertifikasıyla, yalnızca çift katlı bir konfigürasyonda değil, aynı zamanda sabit yük sabitleme konfigürasyonunda da kullanılabilir. çift katlı sistem, sonradan da donatılabilir; daha fazla bilgi için aşağıdan bilgi alabilir veya ihtiyaçlarınızı karşılayan bir konfigürasyon için yerel şubenizle iletişime geçebilirsiniz.
                </Typography>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Second Product Section */}
      <Container maxWidth="xl" sx={{ py: 12, px: { xs: 2, md: 4, lg: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
          <Grid item xs={12} lg={6}>
            <Fade in timeout={1000}>
              <Box>
                <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 4 }}>
                  Römorklarınızın İçindeki Alanı En Üst Düzeye Çıkarın
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Çift katlı sistem ile her milimetreyi sonuna kadar kullanabileceksiniz. Piyasadaki en küçük ayarlanabilir eğimle birlikte en düşük palet profiline sahiptir. Palet ve kiriş arasında neredeyse hiç sürtünme olmadığı için aşınma ve yıpranma riski yoktur ve yıl boyunca maksimum performans sağlar. İsteğe bağlı olarak PIEK sertifikasıyla da mevcuttur!
                </Typography>
              </Box>
            </Fade>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Fade in timeout={1500}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    transition: 'transform 0.3s ease',
                  }
                }}
              >
                <LoadingImage
                  src="/35.jpg"
                  alt="LayerLok XP Performans"
                  style={{
                    width: '100%',
                    height: '400px',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Process Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 12 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Box textAlign="center" mb={8}>
            <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
              Çift Katlı Sistem Montaj Sürecimiz
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Çift katlı sistem montajını nasıl gerçekleştirdiğimizi keşfedin
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {process.map((step, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {step.step}
                      </Box>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {step.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default DoubleDeck;
